/**
 * ErrorBoundary — последний рубеж на случай JS-исключений в рендере.
 *
 * Без него ошибка в любом компоненте (упал i18n, упал motion, упал
 * React-хук) приводила к белому/чёрному экрану навсегда — юзер не мог
 * ни перезагрузить, ни увидеть что произошло. Теперь показываем
 * fallback с кнопкой «Перезагрузить» — особенно важно для слабых
 * Android-вебвью где реалия бага выше.
 */
import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  handleReload = (): void => {
    try {
      window.sessionStorage.removeItem('luna_loader_done');
    } catch {
      /* silent */
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: '#050612',
            color: '#fff',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 18 }}>⚠️</div>
          <h1
            style={{
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 8,
              letterSpacing: '-0.01em',
            }}
          >
            Что-то пошло не так
          </h1>
          <p
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.65)',
              marginBottom: 24,
              maxWidth: 320,
              lineHeight: 1.5,
            }}
          >
            Приложение не смогло запуститься. Попробуйте перезагрузить —
            если не помогло, закройте Mini App и откройте заново.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '12px 28px',
              borderRadius: 14,
              border: 'none',
              background: 'linear-gradient(180deg, #F5D97E, #E8B344)',
              color: '#0a0a0f',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 8px 24px -8px rgba(245,217,126,0.5)',
            }}
          >
            Перезагрузить
          </button>
          {import.meta.env.DEV && (
            <pre
              style={{
                marginTop: 24,
                fontSize: 10,
                color: 'rgba(255,255,255,0.4)',
                maxWidth: '90vw',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
